import { useLayoutEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEffectiveTenant } from '@shared/hooks/useEffectiveTenant';

/**
 * Phase 51 D-01 cache isolation (research Pattern 5 / A4).
 *
 * Whenever the effective tenant changes — the platform_admin switches the selection, or crosses
 * between /admin and non-admin pages while overridden — running queries are cancelled and the
 * whole TanStack Query cache is reset BEFORE the next paint (`useLayoutEffect`), so data of the
 * previous tenant is never shown. A blanket reset is deliberate: hundreds of generated hooks have
 * query keys that carry no tenant. Tenant-domain hooks additionally carry the effective tenant in
 * their keys.
 *
 * The first observation, and the transition from "identity not known yet" (null) to the home
 * tenant, are not changes: nothing tenant-specific can be cached by then.
 */
export function TenantCacheBridge(): null {
  const { effectiveTenantId } = useEffectiveTenant();
  const queryClient = useQueryClient();
  const previous = useRef<string | null | undefined>(undefined);

  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = effectiveTenantId;
    if (before === undefined || before === null || before === effectiveTenantId) return;
    void queryClient.cancelQueries();
    void queryClient.resetQueries();
  }, [effectiveTenantId, queryClient]);

  return null;
}

/** Router root element: the cache bridge next to the matched child route. */
export function TenantCacheBridgeOutlet(): JSX.Element {
  return (
    <>
      <TenantCacheBridge />
      <Outlet />
    </>
  );
}
